import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { AdminAssistant } from "@/components/admin/AdminAssistant";
import { BillingCard } from "@/components/admin/BillingCard";
import { AccessCard } from "@/components/admin/AccessCard";
import { OutboundCallCard } from "@/components/admin/OutboundCallCard";
import { UsersTable } from "@/components/admin/UsersTable";
import { UnmatchedReports } from "@/components/admin/UnmatchedReports";
import { PromptLibrary } from "@/components/admin/PromptLibrary";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unmatched, setUnmatched] = useState([]);
  const [unmatchedLoading, setUnmatchedLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [u, um] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/unmatched-incidents"),
      ]);
      setUsers(u.data);
      setUnmatched(um.data);
    } catch {
      toast.error("Could not load admin data");
    } finally {
      setLoading(false);
      setUnmatchedLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const onGranted = (userId, credits) => {
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, credits } : u)));
  };

  const onDeleted = (userId) => {
    setUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  return (
    <Layout>
      <div className="max-w-5xl w-full mx-auto px-6 py-8 space-y-6" data-testid="admin-page">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 text-[#007AFF]" /> Admin
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter">User Credits</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
            Grant call credits to any user — including yourself. Grants appear in the user's purchase
            history as "Admin grant" and are usable immediately.
          </p>
        </div>

        <AdminAssistant />

        <Link to="/admin/moderation" data-testid="admin-moderation-link"
          className="group flex items-center justify-between gap-4 border border-border bg-[#0A0A0A] rounded-sm p-6 hover:border-[#FF3B30]/60 transition-colors duration-200">
          <div>
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-[#FF3B30]" />
              <h2 className="font-heading text-lg font-black tracking-tight">Moderation</h2>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Manage the flag library, review blocked outgoing messages, and issue strikes.
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-white group-hover:translate-x-1 transition-all duration-200" />
        </Link>

        <AccessCard />

        <BillingCard />

        <OutboundCallCard />

        <PromptLibrary />

        <UsersTable users={users} loading={loading} currentUserId={user?.id}
          onGranted={onGranted} onDeleted={onDeleted} />

        <UnmatchedReports incidents={unmatched} loading={unmatchedLoading} />
      </div>
    </Layout>
  );
}
